"use client";

import Image from "next/image";
import { User } from "lucide-react";

interface AvatarProps {
  src?: string;
  alt?: string;
  name?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Custom color for the ring/background */
  color?: string;
  /** Show ring around avatar */
  showRing?: boolean;
  /** Show editing indicator */
  isEditing?: boolean;
  className?: string;
}

const sizeStyles: Record<"xs" | "sm" | "md" | "lg" | "xl", { container: string; icon: number; text: string }> = {
  xs: { container: "w-5 h-5", icon: 12, text: "text-[10px]" },
  sm: { container: "w-6 h-6", icon: 14, text: "text-xs" },
  md: { container: "w-7 h-7", icon: 16, text: "text-sm" },
  lg: { container: "w-9 h-9", icon: 20, text: "text-base" },
  xl: { container: "w-12 h-12", icon: 24, text: "text-lg" },
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function Avatar({
  src,
  alt = "",
  name,
  size = "md",
  color,
  showRing = false,
  isEditing = false,
  className = "",
}: AvatarProps) {
  const sizeStyle = sizeStyles[size];
  const initials = name ? getInitials(name) : "";

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`
          ${sizeStyle.container}
          relative rounded-full overflow-hidden
          flex items-center justify-center
          ${showRing ? "ring-2 ring-white shadow-sm" : ""}
        `}
        style={color ? { backgroundColor: color } : undefined}
      >
        {src ? (
          <Image
            fill
            src={src}
            alt={alt || name || "Avatar"}
            className="object-cover"
            sizes="48px"
          />
        ) : initials ? (
          <span className={`font-medium text-white ${sizeStyle.text}`}>
            {initials}
          </span>
        ) : (
          <div className="w-full h-full bg-neutral-200 flex items-center justify-center">
            <User width={sizeStyle.icon} height={sizeStyle.icon} className="text-neutral-500" />
          </div>
        )}
      </div>

      {/* Editing indicator */}
      {isEditing && (
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white animate-pulse" />
      )}
    </div>
  );
}

export { type AvatarProps };
